import { Chart, ChartOptions } from 'chart.js'
import { getElementAtEvent, Line } from 'react-chartjs-2'
import ChartsHelper, { ChartDatasetWithUnit, ChartDataWithUnit } from './ChartsHelper'
import Helper from '../../helpers/Helper'
import Formatter from '../../helpers/Formatter'
import _ from 'lodash'
import { useRef } from 'react'

interface Props {
    data: ChartDataWithUnit<'line', number[], unknown>
    options?: any
    moreOptions?: any
    title?: string
    isLoading?: boolean
    onClickSerie?: (options: { seriesName: string, index: number }) => void
}

const LineChart = ({ data, options, moreOptions, title, isLoading, onClickSerie }: Props) => {
    let myOptions = options ?? { ...LineChartDefaultOptions, ...moreOptions }
    let chartData = data

    const chartRef = useRef<Chart<'line', number[], unknown>>(null)

    const handleChartHover = (event: any, activeElements: Array<any>) => {
        const canvas = chartRef.current?.canvas

        if (activeElements.length > 0 && Helper.hasVal(onClickSerie)) {
            canvas.style.cursor = 'pointer'
        } else {
            canvas.style.cursor = 'default'
        }
    }

    const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
        const myChart = chartRef.current
        const elements = getElementAtEvent(myChart, event)

        if (Helper.hasVal(elements) && elements.length > 0) {
            const datasetIndex = elements[0].datasetIndex
            const dataIndex = elements[0].index

            if (onClickSerie)
                onClickSerie({ index: dataIndex, seriesName: data.datasets[datasetIndex].label })
        }
    }

    if (title)
        myOptions.plugins.title = { text: title, display: true, align: 'start' }

    if (isLoading) {
        chartData = {
            datasets: [
                { data: Array(12).fill(0)?.map(() => Math.random() * 5), order: 1 }
            ],
            labels: Array(12).fill('')
        }

        myOptions = {
            ...myOptions,
            scales: { y: { display: false }, y2: { display: false } },
            plugins: {
                legend: { display: false },
                datalabels: { display: false }
            }
        }
    }

    myOptions.plugins.tooltip = {
        callbacks: {
            label: function (context: any) {
                const dataset = chartData.datasets[context.datasetIndex] as ChartDatasetWithUnit
                const value = context.raw
                const unit = dataset.unit ?? ''

                if (Helper.isNumber(value))
                    return unit === '%' ? `${Formatter.number(value)}${unit}` : `${unit}${Formatter.number(value)}`

                return `${value}`
            }
        }
    }

    myOptions.scales.y.ticks = {
        callback: function (value: any) {
            const unit = chartData.datasets[0]?.unit ?? ''
            if (Helper.isNumber(value))
                return unit === '%' ? `${Formatter.compact(value)}${unit}` : `${unit}${Formatter.compact(value)}`

            return `${value}`
        }
    }

    ChartsHelper.applyColors(chartData.datasets as any, isLoading)
    ChartsHelper.applyMode(myOptions)

    return (
        <div style={{ flex: 1, height: '100%', width: '100%' }}>
            <Line
                ref={chartRef}
                data={chartData}
                width='100%'
                height='100%'
                options={{
                    ...myOptions,
                    onHover: (event, activeElements) => handleChartHover(event.native, activeElements)
                }}
                onClick={handleChartClick}
            />
        </div>
    )
}

export default LineChart

const LineChartDefaultOptions: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    plugins: {
        tooltip: { callbacks: {} },
        datalabels: { display: false }
    },
    elements: {
        line: { tension: 0.5 }
    },
    scales: {
        x: {
            grid: { display: false }
        },
        y: {
            grid: { color: 'rgba(166,166,166, 0.35)' },
            beginAtZero: true,
            ticks: {
                font: { size: 11 },
                callback(tickValue) {
                    const formatter = Intl.NumberFormat('en', { notation: 'compact' })
                    return formatter.format(tickValue as number)
                }
            }
        }
    }
}
export const GetLineChartDefaultOptions = () => _.cloneDeep(LineChartDefaultOptions)
